// frontend/src/pages/P2PTradeChat.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import p2pAxios from '../p2pAxios';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axiosAuth from '../axiosConfig';
import socket from '../socket';

/* ─────────────────────────────── UI Helpers (for mobile only) ─────────────────────────────── */
const Section = ({ title, children, className = '' }) => (
  <div className={`bg-white dark:bg-neutral-900 border dark:border-neutral-800 rounded-xl shadow-sm ${className}`}>
    <div className="px-4 py-3 border-b dark:border-neutral-800">
      <h3 className="text-sm sm:text-base font-semibold text-blue-700 dark:text-blue-300">{title}</h3>
    </div>
    <div className="px-4 py-3">{children}</div>
  </div>
);

const Accordion = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white dark:bg-neutral-900 border dark:border-neutral-800 rounded-xl shadow-sm">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full px-4 py-3 flex items-center justify-between text-left"
      >
        <span className="text-sm sm:text-base font-semibold text-blue-700 dark:text-blue-300">{title}</span>
        <span className="text-gray-600 dark:text-gray-300 text-xs sm:text-sm">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && <div className="px-4 pb-3">{children}</div>}
    </div>
  );
};

const mobileBtn =
  'text-xs px-3 py-2 rounded-lg font-semibold shadow focus:outline-none focus:ring transition';

/* ─────────────────────────────── Tiny Utils ─────────────────────────────── */
const fmt2 = (n) => (n < 10 ? `0${n}` : `${n}`);
const fmtCountdown = (ms) => {
  if (ms <= 0) return '00:00';
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${fmt2(m)}:${fmt2(s)}`;
};

// Best-effort stable key for chat rows (prevents weird double-render)
const msgKey = (m) => {
  const id = m?._id || m?.id;
  if (id) return String(id);
  const t = m?.timestamp || m?.createdAt || '';
  const sid = m?.sender?._id || m?.sender?.id || m?.sender || '';
  const text = m?.message || '';
  const img = m?.imageUrl || m?.image || '';
  return `${String(t)}|${String(sid)}|${String(text)}|${String(img)}`.slice(0, 220);
};

const normalizeMsg = (m) => {
  if (!m) return m;
  return {
    ...m,
    timestamp: m.timestamp || m.createdAt || new Date().toISOString(),
  };
};

const isSystemMsg = (msg) =>
  Boolean(msg?.isSystem) ||
  Boolean(msg?.system) ||
  msg?.role === 'system' ||
  msg?.type === 'system' ||
  msg?.sender === 'system' ||
  msg?.sender == null;

/* ─────────────────────────────── Memo Chat Row ─────────────────────────────── */
const ChatRow = React.memo(function ChatRow({
  msg,
  currentUserId,
  onOpenImage,
}) {
  const isSystem = isSystemMsg(msg);
  const isMine = msg?.sender && msg?.sender?._id === currentUserId;

  const time = useMemo(() => {
    try {
      return new Date(msg?.timestamp).toLocaleString('en-IN', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  }, [msg?.timestamp]);

  const image = msg?.imageUrl || msg?.image || '';

  // System message
  if (isSystem) {
    return (
      <div className="flex justify-center mb-2 sm:mb-3">
        <div className="bg-yellow-100 dark:bg-yellow-900/40 border border-yellow-400 dark:border-yellow-800 text-yellow-900 dark:text-yellow-200 px-3 sm:px-4 py-2 rounded-lg text-[11px] sm:text-sm shadow-md text-center max-w-md">
          <p className="font-semibold mb-1">⚠️ System Message</p>
          <p className="break-words">{msg?.message}</p>
          <p className="text-[10px] mt-1 opacity-70">{time}</p>
        </div>
      </div>
    );
  }

  // Normal message
  return (
    <div className={`flex ${isMine ? 'justify-end' : 'justify-start'} mb-1.5 sm:mb-2`}>
      <div
        className={`max-w-[82%] sm:max-w-xs px-3 sm:px-4 py-2 rounded-lg shadow-md ${
          isMine
            ? 'bg-blue-600 text-white rounded-br-none'
            : 'bg-gray-200 dark:bg-neutral-700 text-gray-900 dark:text-gray-50 rounded-bl-none'
        }`}
        style={{ willChange: 'transform' }}
      >
        <p className="text-[10px] sm:text-xs font-semibold mb-1 opacity-95">
          {msg?.sender?.name || msg?.sender?.username || 'User'}
        </p>

        {/* Image attachment: small thumbnail for smoothness */}
        {image ? (
          <button
            type="button"
            onClick={() => onOpenImage(image)}
            className="block mt-2 text-left"
            title="Open image"
          >
            <img
              src={image}
              alt="attachment"
              className="rounded-lg border border-blue-200 dark:border-neutral-700 shadow object-cover w-28 h-28 sm:w-36 sm:h-36"
              loading="lazy"
              decoding="async"
              draggable={false}
            />
            <div className="text-[10px] mt-1 opacity-80 underline">Tap to open</div>
          </button>
        ) : null}

        {msg?.message ? (
          <p className="text-xs sm:text-sm break-words whitespace-pre-wrap">{msg.message}</p>
        ) : null}

        <p className="text-[10px] text-right mt-1 opacity-80 tabular-nums">{time}</p>
      </div>
    </div>
  );
});

/* ─────────────────────────────── Component ─────────────────────────────── */
const P2PTradeChat = () => {
  const { tradeId } = useParams();

  const [trade, setTrade] = useState(null);
  const [sellerBank, setSellerBank] = useState(null);

  // raw messages in state (we dedupe before render)
  const [messages, setMessages] = useState([]);

  const [newMessage, setNewMessage] = useState('');

  const [attachment, setAttachment] = useState(null); // File (image)
  const [attachmentPreview, setAttachmentPreview] = useState(''); // objectURL
  const fileInputRef = useRef(null);

  const [sending, setSending] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');

  const chatListRef = useRef(null);

  // Image viewer modal
  const [imageModalUrl, setImageModalUrl] = useState('');
  const openImage = useCallback((url) => {
    if (!url) return;
    setImageModalUrl(url);
  }, []);
  const closeImage = useCallback(() => setImageModalUrl(''), []);

  // feedback UI state
  const [fbType, setFbType] = useState('positive');  // 'positive' | 'neutral' | 'negative'
  const [fbComment, setFbComment] = useState('');
  const [fbSubmitting, setFbSubmitting] = useState(false);
  const [fbDone, setFbDone] = useState(false);
  const [fbMsg, setFbMsg] = useState('');

  const [myFb, setMyFb] = useState(null);   // server copy of my feedback (if exists)
  const [fbEditing, setFbEditing] = useState(false);

  // NEW state (expiry)
  const [isExpired, setIsExpired] = useState(false);
  const [countdown, setCountdown] = useState('');
  const [warnedFive, setWarnedFive] = useState(false); // client-side 5-min warning once

  // helper: profile link
  const profilePath = (uname) =>
    uname ? `/p2p/user/${encodeURIComponent(uname)}` : '#';

  const isCompleted = (s) => String(s || '').toLowerCase() === 'completed';

  // Helper: set UI to Expired immediately
  const flipToExpired = useCallback(() => {
    setIsExpired(true);
    setCountdown('00:00');
    setTrade(prev => (prev ? { ...prev, status: 'expired' } : prev));
    setStatusMessage('⏰ Trade expired (no payment marked).');
  }, []);

  // Cleanup object URL on unmount / change
  useEffect(() => {
    return () => {
      try {
        if (attachmentPreview) URL.revokeObjectURL(attachmentPreview);
      } catch {}
    };
  }, [attachmentPreview]);

  // Close modal on ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') closeImage();
    };
    if (imageModalUrl) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [imageModalUrl, closeImage]);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const res = await axiosAuth.get('/whoami');
      setCurrentUser(res.data.user);
    } catch (err) {
      console.error('❌ Failed to fetch current user:', err);
    }
  }, []);

  const fetchTrade = useCallback(async () => {
    try {
      const res = await p2pAxios.get(`/trades/${tradeId}`);
      setTrade(res.data.trade);
      setSellerBank(res.data.sellerBank || null);
    } catch (err) {
      console.error('❌ Error fetching trade:', err);
    } finally {
      setLoading(false);
    }
  }, [tradeId]);

  const dedupeAndSetMessages = useCallback((arr) => {
    const map = new Map();
    (arr || []).forEach((raw) => {
      const nm = normalizeMsg(raw);
      const k = msgKey(nm);
      // keep newest version if duplicates arrive
      const existing = map.get(k);
      if (!existing) map.set(k, nm);
      else {
        const et = new Date(existing.timestamp || existing.createdAt || 0).getTime();
        const nt = new Date(nm.timestamp || nm.createdAt || 0).getTime();
        map.set(k, nt >= et ? nm : existing);
      }
    });
    const out = Array.from(map.values()).sort((a, b) => {
      const at = new Date(a.timestamp || a.createdAt || 0).getTime();
      const bt = new Date(b.timestamp || b.createdAt || 0).getTime();
      return at - bt;
    });
    setMessages(out);
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await p2pAxios.get(`/messages/${tradeId}`);
      const list = res.data.messages || [];
      dedupeAndSetMessages(list);
    } catch (err) {
      console.error('❌ Failed to fetch messages:', err);
    }
  }, [tradeId, dedupeAndSetMessages]);

  useEffect(() => {
    fetchCurrentUser();
    fetchTrade();
    fetchMessages();
  }, [tradeId, fetchCurrentUser, fetchTrade, fetchMessages]);

  // Countdown
  useEffect(() => {
    const active = trade?.status === 'initiated' && trade?.expiresAt;
    if (!active) {
      setCountdown('');
      setWarnedFive(false);
      return;
    }

    const end = new Date(trade.expiresAt).getTime();

    const tick = () => {
      const ms = end - Date.now();
      if (ms <= 0) {
        flipToExpired();
        clearInterval(timer);
      } else {
        setIsExpired(false);
        setCountdown(fmtCountdown(ms));
        if (!warnedFive && ms <= 5 * 60 * 1000) {
          setWarnedFive(true);
          setStatusMessage('⏳ Trade is expiring in 5 minutes. Please mark as paid if you’ve sent payment.');
        }
      }
    };

    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [trade?.status, trade?.expiresAt, warnedFive, flipToExpired]);

  const pickAttachment = useCallback((file) => {
    if (!file) return;

    if (!String(file.type || '').startsWith('image/')) {
      alert('Please select an image (screenshot).');
      return;
    }

    const maxBytes = 8 * 1024 * 1024;
    if (file.size && file.size > maxBytes) {
      alert('Image too large. Please upload a screenshot under 8MB.');
      return;
    }

    try {
      if (attachmentPreview) URL.revokeObjectURL(attachmentPreview);
    } catch {}

    setAttachment(file);
    try {
      const url = URL.createObjectURL(file);
      setAttachmentPreview(url);
    } catch {
      setAttachmentPreview('');
    }
  }, [attachmentPreview]);

  const clearAttachment = useCallback(() => {
    try {
      if (attachmentPreview) URL.revokeObjectURL(attachmentPreview);
    } catch {}
    setAttachment(null);
    setAttachmentPreview('');
  }, [attachmentPreview]);

  const onMessageKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Smooth autoscroll only if user is near bottom
  const shouldAutoScrollRef = useRef(true);
  const onChatScroll = useCallback(() => {
    const el = chatListRef.current;
    if (!el) return;
    const threshold = 140; // px from bottom
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    shouldAutoScrollRef.current = dist < threshold;
  }, []);

  const scrollToBottom = useCallback((behavior = 'auto') => {
    const el = chatListRef.current;
    if (!el) return;
    // next paint: makes it smoother on heavy lists
    requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior });
    });
  }, []);

  // DEDUPED messages for render (extra safety)
  const renderMessages = useMemo(() => {
    const map = new Map();
    (messages || []).forEach((m) => {
      const nm = normalizeMsg(m);
      map.set(msgKey(nm), nm);
    });
    return Array.from(map.values()).sort((a, b) => {
      const at = new Date(a.timestamp || a.createdAt || 0).getTime();
      const bt = new Date(b.timestamp || b.createdAt || 0).getTime();
      return at - bt;
    });
  }, [messages]);

  // When renderMessages changes, auto-scroll if user was near bottom
  useEffect(() => {
    if (shouldAutoScrollRef.current) scrollToBottom('smooth');
  }, [renderMessages.length, scrollToBottom]);

  const handleSend = useCallback(async () => {
    const text = String(newMessage || '').trim();
    if (!text && !attachment) return;

    setSending(true);
    try {
      const formData = new FormData();
      formData.append('tradeId', tradeId);

      if (text) formData.append('message', text);

      if (attachment) {
        formData.append('attachment', attachment);
        formData.append('file', attachment);
      }

      await p2pAxios.post('/messages/send', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setNewMessage('');
      if (attachmentPreview) {
        try { URL.revokeObjectURL(attachmentPreview); } catch {}
      }
      setAttachment(null);
      setAttachmentPreview('');

      // We keep your logic (server is source of truth)
      // Dedupe will prevent “double” even if socket also arrived.
      fetchMessages();
      // If user is at bottom, keep it feeling instant
      scrollToBottom('smooth');
    } catch (err) {
      console.error('❌ Failed to send message:', err.response?.data || err.message);
    }
    setSending(false);
  }, [newMessage, attachment, tradeId, attachmentPreview, fetchMessages, scrollToBottom]);

  // Feedback checks
  useEffect(() => {
    const checkMine = async () => {
      if (!trade?._id) return;
      try {
        const r = await p2pAxios.get(`/feedback/mine/${trade._id}`);
        if (r.data?.feedback) {
          setFbDone(true);
          setFbMsg('You have already left feedback for this trade.');
        }
      } catch {}
    };
    if (trade && isCompleted(trade.status)) checkMine();
  }, [trade]);

  const submitFeedbackCreate = async (e) => {
    e.preventDefault();
    if (!trade?._id) return;
    setFbSubmitting(true);
    setFbMsg('');
    try {
      const res = await p2pAxios.post('/feedback/leave', {
        tradeId: trade._id,
        type: fbType,
        comment: fbComment.trim(),
      });
      if (res.data?.success) {
        setMyFb(res.data.feedback);
        setFbDone(true);
        setFbEditing(false);
        setFbMsg('Thanks! Your feedback has been recorded.');
      } else {
        setFbMsg(res.data?.error || 'Could not submit feedback.');
      }
    } catch (err) {
      const code = err?.response?.status;
      const msg =
        err?.response?.data?.error ||
        (code === 409 ? 'You already left feedback for this trade.' : 'Failed to submit feedback.');
      setFbMsg(msg);
      if (code === 409) setFbDone(true);
    } finally {
      setFbSubmitting(false);
    }
  };

  const submitFeedbackUpdate = async (e) => {
    e.preventDefault();
    if (!trade?._id) return;
    setFbSubmitting(true);
    setFbMsg('');
    try {
      const res = await p2pAxios.put(`/feedback/mine/${trade._id}`, {
        type: fbType,
        comment: fbComment.trim(),
      });
      if (res.data?.success) {
        setMyFb(res.data.feedback);
        setFbEditing(false);
        setFbDone(true);
        setFbMsg('Feedback updated.');
      } else {
        setFbMsg(res.data?.error || 'Could not update feedback.');
      }
    } catch (err) {
      setFbMsg(err?.response?.data?.error || 'Failed to update feedback.');
    } finally {
      setFbSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchMine = async () => {
      try {
        if (!trade?._id || !isCompleted(trade?.status)) return;
        const r = await p2pAxios.get(`/feedback/mine/${trade._id}`);
        const f = r.data?.feedback || null;
        setMyFb(f);
        if (f) {
          setFbType(f.type);
          setFbComment(f.comment || '');
          setFbDone(true);
          setFbMsg('');
          setFbEditing(false);
        } else {
          setFbDone(false);
        }
      } catch (e) {
        setMyFb(null);
      }
    };
    fetchMine();
  }, [trade]);

  const markPaid = async () => {
    try {
      await p2pAxios.post(`/trades/mark-paid/${tradeId}`);
      setStatusMessage('✅ Marked as paid. Waiting for seller to release.');
      setTrade(prev => ({ ...prev, status: 'paid', expiresAt: null }));
    } catch (err) {
      console.error('❌ Failed to mark paid:', err);
    }
  };

  const releaseUSDT = async () => {
    try {
      await p2pAxios.post(`/trades/release/${tradeId}`);
      setTrade(prev => ({ ...prev, status: "completed" }));
      setStatusMessage('✅ USDT released successfully.');
    } catch (err) {
      console.error('❌ Failed to release USDT:', err);
    }
  };

  const handleCancel = async () => {
    try {
      await p2pAxios.post(`/trades/${tradeId}/cancel`);
      setStatusMessage('❌ Trade cancelled successfully.');
      setTrade(prev => ({ ...prev, status: 'cancelled' }));
      setTimeout(() => {
        fetchTrade();
      }, 500);
    } catch (err) {
      console.error('❌ Failed to cancel trade:', err);
    }
  };

  // Socket wiring
  useEffect(() => {
    if (!trade || !trade._id) return;

    socket.emit('join-trade', trade._id);

    const onPaid = (data) => {
      if (data.tradeId === trade._id) {
        setTrade(prev => ({ ...prev, status: 'paid', expiresAt: null }));
        setStatusMessage('✅ Buyer has marked the trade as paid.');
      }
    };

    const onCancelled = (data) => {
      if (data.tradeId === trade._id) {
        setTrade(prev => ({ ...prev, status: 'cancelled' }));
        setStatusMessage('❌ This trade has been cancelled.');
      }
    };

    const onExpiring = (data) => {
      if (data.tradeId?.toString() === trade._id?.toString()) {
        setStatusMessage(`⏳ Trade is expiring in ${data.minutesLeft} minutes. Please mark as paid if you’ve sent payment.`);
      }
    };

    const onExpired = (data) => {
      if (data.tradeId === trade._id) {
        flipToExpired();
      }
    };

    const onReleased = (data) => {
      if (data.tradeId === trade._id) {
        setTrade(prev => ({ ...prev, status: 'completed' }));
        setStatusMessage('✅ Seller has released the crypto.');
      }
    };

    const onDisputeResolved = (data) => {
      if (data.tradeId?.toString() !== trade._id?.toString()) return;
      setStatusMessage(
        `🏁 Dispute resolved • ${data.winner === 'buyer' ? 'Buyer wins' : 'Seller wins'}${data.note ? ' • ' + data.note : ''}`
      );
      setTrade(prev => ({ ...prev, status: data.tradeStatus || prev.status }));
    };

    // IMPORTANT: dedupe incoming socket messages to fix “double messages”
    const onNewMessage = (newMsg) => {
      const incomingTradeId = (newMsg?.tradeId || newMsg?.trade || '').toString();
      if (incomingTradeId !== trade._id.toString()) return;

      const normalized = normalizeMsg(newMsg);

      setMessages(prev => {
        const next = [...(prev || [])];
        const nk = msgKey(normalized);

        // If already exists, don’t add again
        for (let i = next.length - 1; i >= 0; i--) {
          if (msgKey(next[i]) === nk) return prev;
        }

        next.push(normalized);
        return next;
      });

      if (/trade expired/i.test(newMsg?.message || '')) flipToExpired();
    };

    const onDisputeRaised = (data) => {
      if (data.tradeId === trade._id) {
        setTrade(prev => ({ ...prev, status: 'disputed' }));
      }
    };

    const onDisputeCancelled = (data) => {
      if (data.tradeId === trade._id) {
        setTrade(prev => ({ ...prev, status: 'paid' }));
      }
    };

    socket.on('p2p:trade-paid', onPaid);
    socket.on('p2p:trade-cancelled', onCancelled);
    socket.on('p2p:trade-expired', onExpired);
    socket.on("p2p:trade-expiring", onExpiring);
    socket.on('p2p:trade-released', onReleased);
    socket.on('p2p:new-message', onNewMessage);
    socket.on('p2p:dispute-raised', onDisputeRaised);
    socket.on('p2p:dispute-cancelled', onDisputeCancelled);
    socket.on('p2p:dispute-resolved', onDisputeResolved);

    return () => {
      socket.emit('leave-trade', trade._id);
      socket.off('p2p:trade-paid', onPaid);
      socket.off('p2p:trade-cancelled', onCancelled);
      socket.off('p2p:trade-expired', onExpired);
      socket.off("p2p:trade-expiring", onExpiring);
      socket.off('p2p:trade-released', onReleased);
      socket.off('p2p:new-message', onNewMessage);
      socket.off('p2p:dispute-raised', onDisputeRaised);
      socket.off('p2p:dispute-cancelled', onDisputeCancelled);
      socket.off('p2p:dispute-resolved', onDisputeResolved);
    };
  }, [trade, flipToExpired]);

  const isBuyer = currentUser?._id === trade?.buyer?._id;

  if (loading && !trade) {
    return (
      <div className="min-h-screen bg-blue-50 dark:bg-neutral-950 flex items-center justify-center px-4">
        <div className="bg-white dark:bg-neutral-900 border dark:border-neutral-800 rounded-xl shadow p-6 w-full max-w-md text-center">
          <div className="text-xl font-semibold text-blue-700 dark:text-blue-300">Loading trade…</div>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">Please wait.</div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="min-h-screen bg-blue-50 dark:bg-neutral-950 py-8 px-4 transition-colors"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Image Modal */}
      {imageModalUrl && (
        <div
          className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            // close only if clicking backdrop
            if (e.target === e.currentTarget) closeImage();
          }}
        >
          <div className="max-w-4xl w-full">
            <div className="flex items-center justify-between mb-3">
              <div className="text-white text-sm opacity-90">Screenshot preview</div>
              <button
                type="button"
                onClick={closeImage}
                className="text-white bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg text-sm"
              >
                Close ✕
              </button>
            </div>
            <div className="bg-black rounded-xl overflow-hidden shadow-2xl border border-white/10">
              <img
                src={imageModalUrl}
                alt="full preview"
                className="w-full max-h-[75vh] object-contain bg-black"
                loading="eager"
                draggable={false}
              />
            </div>
          </div>
        </div>
      )}

      {/* ⬅ Go Back Link */}
      <motion.div
        className="max-w-7xl mx-auto mb-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        <Link
          to="/p2p"
          className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline font-medium"
        >
          ⬅ Go Back to P2P Hub
        </Link>
      </motion.div>

      {trade && (
        <div className="max-w-7xl mx-auto mb-3">
          <div className="text-sm text-gray-600 dark:text-gray-300">
            <strong>Trade ID:</strong> {trade._id}
          </div>
        </div>
      )}

              {trade?.payableFiatInr && currentUser && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="max-w-7xl mx-auto mb-5"
          >
            {/* 🟢 DISPUTE RESOLVED (TOP PRIORITY) */}
            {trade.status === 'dispute_resolved' ? (
              <div className="bg-green-600 text-white px-4 py-3 rounded-xl shadow-md text-center text-lg font-semibold">
                🏁 Dispute Resolved
                {trade?.disputeResolution?.outcome && (
                  <div className="text-sm mt-1 opacity-90">
                    {trade.disputeResolution.outcome === 'buyer_won' && 'Buyer won the dispute'}
                    {trade.disputeResolution.outcome === 'seller_won' && 'Seller won the dispute'}
                  </div>
                )}
              </div>
            ) : trade.status === 'disputed' ? (
              <div className="bg-red-600 text-white px-4 py-3 rounded-xl shadow-md text-center text-lg font-semibold">
                ⚠️ Dispute Opened – This trade is being reviewed.
              </div>
            ) : (
              <div className="bg-blue-600 text-white px-4 py-3 rounded-xl shadow-md">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="text-center sm:text-left text-xl md:text-2xl font-semibold">
                    {(() => {
                      const amount = Number(trade.payableFiatInr).toLocaleString('en-IN');
                      const status = String(trade.status || '').toLowerCase();

                      if (isExpired || status === 'expired') return '⏰ Trade Expired';
                      if (status === 'initiated') {
                        return isBuyer
                          ? `💡 Buyer, you need to pay ₹${amount} to the seller.`
                          : `💡 Buyer is paying ₹${amount} to you.`;
                      }
                      if (status === 'paid') return '✅ Trade Paid';
                      if (status === 'completed') return '✅ Trade Completed';
                      if (status === 'cancelled' || status === 'cancelled_after_paid')
                        return '❌ Trade Cancelled';
                      return '';
                    })()}
                  </div>

                  {trade.status === 'initiated' && trade.expiresAt && !isExpired && (
                    <div
                      className={`flex items-center justify-end ${isExpired ? 'text-red-200' : 'text-yellow-200'}`}
                      title={new Date(trade.expiresAt).toLocaleString()}
                    >
                      <span className="mr-2 hidden sm:inline">⏱️</span>
                      <span className="font-bold tabular-nums text-lg md:text-xl">
                        {countdown || '—'}
                      </span>
                    </div>
                  )}
                </div>

                {statusMessage && (
                  <div className="mt-2 text-xs md:text-sm opacity-90">
                    {statusMessage}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        )}

      <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-6">
        {/* ───────────── Left Side: Trade Info ───────────── */}
        {/* MOBILE VERSION (compact, accordions) */}
        <div className="md:col-span-2 space-y-4 sm:hidden">
          <Section title="Summary">
            <div className="grid grid-cols-2 gap-2 text-xs text-gray-800 dark:text-gray-200">
              <div><span className="text-gray-500 dark:text-gray-400">Status:</span> <span className="font-medium">{trade?.status}</span></div>
              <div><span className="text-gray-500 dark:text-gray-400">Coin:</span> <span className="font-medium">{trade?.coin}</span></div>
              <div><span className="text-gray-500 dark:text-gray-400">Price:</span> <span className="font-medium">₹{trade?.price}</span></div>
              <div><span className="text-gray-500 dark:text-gray-400">Net to Bank:</span> <span className="font-medium">₹{trade?.payableFiatInr}</span></div>
            </div>
          </Section>

          <Accordion title="Trade Details" defaultOpen={false}>
            <div className="space-y-1 text-xs text-gray-800 dark:text-gray-200">
              <p><strong>Total Trade Amount:</strong> ₹{trade?.fiatAmount}</p>
              <p><strong>TDS (1%):</strong> ₹{trade?.tdsInr}</p>
              <p><strong>Gross {trade?.coin}:</strong> {Number(trade?.grossUsdt).toFixed(6)} {trade?.coin}</p>
              <p><strong>Platform Fee (Seller):</strong> {Number(trade?.sellerPlatformFeeUsdt).toFixed(6)} {trade?.coin}</p>
              <p><strong>GST on Fee:</strong> {Number(trade?.sellerGstUsdt).toFixed(6)} {trade?.coin}</p>
              <p><strong>Net USDT (Seller):</strong> {Number(trade?.sellerTotalDeductedUsdt).toFixed(6)} {trade?.coin}</p>
            </div>
          </Accordion>

          <Accordion title="Buyer Receives" defaultOpen={false}>
            <div className="space-y-1 text-xs text-gray-800 dark:text-gray-200">
              <p><strong>Buyer Fee:</strong> {Number(trade?.buyerPlatformFeeUsdt).toFixed(6)} {trade?.coin}</p>
              <p><strong>GST on Fee:</strong> {Number(trade?.buyerGstUsdt).toFixed(6)} {trade?.coin}</p>
              <p><strong>Net USDT (Buyer):</strong> {Number(trade?.buyerNetReceiveUsdt).toFixed(6)} {trade?.coin}</p>
            </div>
          </Accordion>

          <Accordion title="Participants" defaultOpen={false}>
            <div className="space-y-1 text-xs text-gray-800 dark:text-gray-200">
              <p>
                <strong>Buyer:</strong>{' '}
                {trade?.buyer?.username ? (
                  <Link to={profilePath(trade.buyer.username)} className="text-blue-600 dark:text-blue-400 hover:underline">
                    @{trade.buyer.username}
                  </Link>
                ) : '—'}
              </p>
              <p>
                <strong>Seller:</strong>{' '}
                {trade?.seller?.username ? (
                  <Link to={profilePath(trade.seller.username)} className="text-blue-600 dark:text-blue-400 hover:underline">
                    @{trade.seller.username}
                  </Link>
                ) : '—'}
              </p>
              <p><strong>Created:</strong> {trade?.createdAt ? new Date(trade.createdAt).toLocaleString() : '—'}</p>
            </div>
          </Accordion>

          <Section title="Seller Bank">
            {sellerBank ? (
              <ul className="text-xs text-gray-700 dark:text-gray-200 space-y-1">
                <li className="flex items-center gap-2">
                  <span><strong>Account Holder:</strong> {sellerBank.accountHolder}</span>
                </li>
                <li className="flex items-center gap-2">
                  <span><strong>Bank Name:</strong> {sellerBank.bankName}</span>
                </li>
                <li className="flex items-center gap-2">
                  <span><strong>Account Number:</strong> {sellerBank.accountNumber}</span>
                  <button
                    type="button"
                    onClick={() => navigator.clipboard?.writeText(sellerBank.accountNumber || '')}
                    className="text-blue-600 dark:text-blue-400 text-[11px] hover:underline"
                    title="Copy account number"
                  >
                    Copy
                  </button>
                </li>
                <li className="flex items-center gap-2">
                  <span><strong>IFSC:</strong> {sellerBank.ifsc}</span>
                  <button
                    type="button"
                    onClick={() => navigator.clipboard?.writeText(sellerBank.ifsc || '')}
                    className="text-blue-600 dark:text-blue-400 text-[11px] hover:underline"
                    title="Copy IFSC"
                  >
                    Copy
                  </button>
                </li>
              </ul>
            ) : (
              <p className="text-red-500 text-xs">Not available</p>
            )}
          </Section>

          {/* FEEDBACK (mobile) */}
          {trade && isCompleted(trade.status) && currentUser && (
            <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 rounded-xl p-3">
              <h3 className="text-sm font-semibold text-blue-700 dark:text-blue-300 mb-2">Rate your counterparty</h3>
              <p className="text-[12px] text-gray-600 dark:text-gray-300 mb-3">
                {(() => {
                  const isBuyerSide = currentUser?._id === trade?.buyer?._id;
                  const other = isBuyerSide ? trade?.seller : trade?.buyer;
                  return <>You’re leaving feedback for <strong>@{other?.username || 'user'}</strong>.</>;
                })()}
              </p>

              {myFb && !fbEditing ? (
                <div className="bg-white dark:bg-neutral-900 border dark:border-neutral-800 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-800 dark:text-gray-200">
                      <div className="font-medium">
                        {myFb.type === 'positive' && '👍 Positive'}
                        {myFb.type === 'neutral'  && '😐 Neutral'}
                        {myFb.type === 'negative' && '👎 Negative'}
                      </div>
                      {myFb.comment && <div className="mt-1">{myFb.comment}</div>}
                      <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                        Left on {new Date(myFb.createdAt || myFb.updatedAt).toLocaleString()}
                      </div>
                    </div>
                    <button
                      onClick={() => setFbEditing(true)}
                      className="ml-3 bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs hover:bg-blue-700"
                    >
                      Edit feedback
                    </button>
                  </div>
                  {fbMsg && <div className="text-xs text-green-700 dark:text-green-400 mt-2">{fbMsg}</div>}
                </div>
              ) : (
                <form onSubmit={myFb ? submitFeedbackUpdate : submitFeedbackCreate} className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {[
                      { v: 'positive', label: '👍 Positive' },
                      { v: 'neutral',  label: '😐 Neutral' },
                      { v: 'negative', label: '👎 Negative' },
                    ].map(opt => (
                      <label
                        key={opt.v}
                        className={`px-3 py-2 rounded-lg border cursor-pointer text-xs ${
                          fbType === opt.v
                            ? 'bg-white dark:bg-neutral-900 border-blue-500 text-blue-700 dark:text-blue-300'
                            : 'bg-white dark:bg-neutral-900 border-gray-300 dark:border-neutral-700 text-gray-700 dark:text-gray-200'
                        }`}
                      >
                        <input
                          type="radio"
                          name="fbType"
                          value={opt.v}
                          checked={fbType === opt.v}
                          onChange={() => setFbType(opt.v)}
                          className="mr-2"
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                      Comment (optional, max 500)
                    </label>
                    <textarea
                      value={fbComment}
                      onChange={(e) => setFbComment(e.target.value.slice(0, 500))}
                      rows={3}
                      className="w-full border rounded-lg px-3 py-2 text-xs bg-white dark:bg-neutral-900 border-gray-300 dark:border-neutral-700 text-gray-800 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-300 dark:focus:ring-blue-500"
                      placeholder="What went well? Anything to improve?"
                      maxLength={500}
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      type="submit"
                      disabled={fbSubmitting}
                      className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-60 text-xs"
                    >
                      {fbSubmitting ? (myFb ? 'Updating…' : 'Submitting…') : (myFb ? 'Update Feedback' : 'Submit Feedback')}
                    </button>

                    {myFb && (
                      <button
                        type="button"
                        onClick={() => {
                          setFbType(myFb.type);
                          setFbComment(myFb.comment || '');
                          setFbEditing(false);
                          setFbMsg('');
                        }}
                        className="bg-gray-200 dark:bg-neutral-800 text-gray-800 dark:text-gray-100 px-3 py-2 rounded-lg hover:bg-gray-300 dark:hover:bg-neutral-700 text-xs"
                      >
                        Cancel
                      </button>
                    )}

                    {fbMsg && <span className="text-xs text-red-600 dark:text-red-400">{fbMsg}</span>}
                  </div>
                </form>
              )}
            </div>
          )}
        </div>

        {/* DESKTOP/TABLET VERSION */}
        <div className="md:col-span-2 space-y-5 hidden sm:block">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Box 1: Trade Financials */}
            <div className="bg-white dark:bg-neutral-900 p-5 border dark:border-neutral-800 rounded-xl shadow-md space-y-2">
              <h3 className="text-lg font-bold text-blue-700 dark:text-blue-300">Trade Details</h3>
              <p className="text-gray-800 dark:text-gray-200"><strong>Status:</strong> {trade?.status}</p>
              <p className="text-gray-800 dark:text-gray-200"><strong>Coin:</strong> {trade?.coin}</p>
              <p className="text-gray-800 dark:text-gray-200"><strong>Price:</strong> ₹{trade?.price}</p>
              <p className="text-gray-800 dark:text-gray-200"><strong>Total Trade Amount:</strong> ₹{trade?.fiatAmount}</p>
              <p className="text-gray-800 dark:text-gray-200"><strong>TDS (1%):</strong> ₹{trade?.tdsInr}</p>
              <p className="text-gray-800 dark:text-gray-200"><strong>Net Credit in Bank:</strong> ₹{trade?.payableFiatInr}</p>
              <p className="text-gray-800 dark:text-gray-200"><strong>Gross {trade?.coin}:</strong> {Number(trade?.grossUsdt).toFixed(6)} {trade?.coin}</p>
              <p className="text-gray-800 dark:text-gray-200"><strong>Platform Fee (Seller):</strong> {Number(trade?.sellerPlatformFeeUsdt).toFixed(6)} {trade?.coin}</p>
              <p className="text-gray-800 dark:text-gray-200"><strong>GST on Fee:</strong> {Number(trade?.sellerGstUsdt).toFixed(6)} {trade?.coin}</p>
              <p className="text-gray-800 dark:text-gray-200"><strong>Net USDT (Seller):</strong> {Number(trade?.sellerTotalDeductedUsdt).toFixed(6)} {trade?.coin}</p>
            </div>

            {/* Box 2: Buyer Fee */}
            <div className="bg-white dark:bg-neutral-900 p-5 border dark:border-neutral-800 rounded-xl shadow-md space-y-2">
              <h3 className="text-lg font-bold text-blue-700 dark:text-blue-300">Buyer Receives</h3>
              <p className="text-gray-800 dark:text-gray-200"><strong>Buyer Fee:</strong> {Number(trade?.buyerPlatformFeeUsdt).toFixed(6)} {trade?.coin}</p>
              <p className="text-gray-800 dark:text-gray-200"><strong>GST on Fee:</strong> {Number(trade?.buyerGstUsdt).toFixed(6)} {trade?.coin}</p>
              <p className="text-gray-800 dark:text-gray-200"><strong>Net USDT (Buyer):</strong> {Number(trade?.buyerNetReceiveUsdt).toFixed(6)} {trade?.coin}</p>
            </div>

            {/* Box 3: Parties */}
            <div className="bg-white dark:bg-neutral-900 p-5 border dark:border-neutral-800 rounded-xl shadow-md space-y-2">
              <h3 className="text-lg font-bold text-blue-700 dark:text-blue-300">Participants</h3>
              <p className="text-gray-800 dark:text-gray-200">
                <strong>Buyer:</strong>{' '}
                {trade?.buyer?.username ? (
                  <Link to={profilePath(trade?.buyer?.username)} className="text-blue-600 hover:underline">
                    @{trade.buyer.username}
                  </Link>
                ) : '—'}
              </p>

              <p className="text-gray-800 dark:text-gray-200">
                <strong>Seller:</strong>{' '}
                {trade?.seller?.username ? (
                  <Link to={profilePath(trade?.seller?.username)} className="text-blue-600 hover:underline">
                    @{trade.seller.username}
                  </Link>
                ) : '—'}
              </p>
              <p className="text-gray-800 dark:text-gray-200"><strong>Created:</strong> {new Date(trade?.createdAt).toLocaleString()}</p>
            </div>

            {/* Box 4: Bank Details */}
            <div className="bg-white dark:bg-neutral-900 p-5 border dark:border-neutral-800 rounded-xl shadow-md">
              <h3 className="text-lg font-bold text-blue-700 dark:text-blue-300 mb-2">Seller Bank</h3>
              {sellerBank ? (
                <ul className="text-gray-700 dark:text-gray-200 space-y-1">
                  <li className="flex items-center gap-2">
                    <span><strong>Account Holder:</strong> {sellerBank.accountHolder}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span><strong>Bank Name:</strong> {sellerBank.bankName}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span><strong>Account Number:</strong> {sellerBank.accountNumber}</span>
                    <button
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(sellerBank.accountNumber || '')}
                      className="text-blue-600 dark:text-blue-400 text-xs hover:underline"
                      title="Copy account number"
                    >
                      Copy
                    </button>
                  </li>
                  <li className="flex items-center gap-2">
                    <span><strong>IFSC:</strong> {sellerBank.ifsc}</span>
                    <button
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(sellerBank.ifsc || '')}
                      className="text-blue-600 dark:text-blue-400 text-xs hover:underline"
                      title="Copy IFSC"
                    >
                      Copy
                    </button>
                  </li>
                </ul>
              ) : (
                <p className="text-red-500">Not available</p>
              )}
            </div>
          </div>

          {/* ===== ACTIONS (DESKTOP/TABLET) ===== */}
          <div className="hidden sm:block">
            {(() => {
              const status = trade?.status;
              const isBuyer =
                currentUser?._id && trade?.buyer?._id && currentUser._id === trade.buyer._id;
              const isSeller =
                currentUser?._id && trade?.seller?._id && currentUser._id === trade.seller._id;

              const raisedBy =
                trade?.dispute?.raisedBy?._id || trade?.dispute?.raisedBy;
              const iAmDisputeRaiser =
                raisedBy && currentUser?._id && raisedBy.toString() === currentUser._id.toString();
              const disputeOpen = trade?.dispute?.status === 'open';

              if (status === 'disputed') {
                if (iAmDisputeRaiser && disputeOpen) {
                  return (
                    <div className="mt-4">
                      <button
                        onClick={async () => {
                          try {
                            await p2pAxios.post('/trades/disputes/cancel', { tradeId: trade._id });
                            setTrade(prev => ({
                              ...prev,
                              status: 'paid',
                              dispute: prev?.dispute ? { ...prev.dispute, status: 'cancelled' } : prev?.dispute
                            }));
                          } catch (err) {
                            console.error('❌ Error cancelling dispute:', err);
                          }
                        }}
                        className="bg-yellow-500 text-white py-3 px-6 rounded-xl w-full font-semibold shadow hover:bg-yellow-600 transition"
                      >
                        🛑 Cancel Dispute
                      </button>
                    </div>
                  );
                }
                return null;
              }

              if (status === 'initiated' && isBuyer) {
                return (
                  <div className="flex flex-col md:flex-row gap-4 mt-4">
                    <button
                      onClick={markPaid}
                      className="bg-yellow-500 text-white py-3 px-6 rounded-xl w-full font-semibold shadow hover:bg-yellow-600 transition"
                    >
                      ✅ Mark as Paid
                    </button>
                    <button
                      onClick={handleCancel}
                      className="bg-red-600 text-white py-3 px-6 rounded-xl w-full font-semibold shadow hover:bg-red-700 transition"
                    >
                      ❌ Cancel Trade
                    </button>
                  </div>
                );
              }

              if (status === 'paid') {
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    {isBuyer && (
                      <>
                        <button
                          onClick={handleCancel}
                          className="bg-red-600 text-white py-3 px-6 rounded-xl w-full font-semibold shadow hover:bg-red-700 transition"
                        >
                          ❌ Cancel Trade
                        </button>
                        <button
                          onClick={() => setShowDisputeModal(true)}
                          className="bg-red-500 text-white py-3 px-6 rounded-xl w-full font-semibold shadow hover:bg-red-600 transition"
                        >
                          ⚠️ Raise Dispute
                        </button>
                      </>
                    )}

                    {isSeller && (
                      <>
                        <button
                          onClick={releaseUSDT}
                          className="bg-green-600 text-white py-3 px-6 rounded-xl w-full font-semibold shadow hover:bg-green-700 transition"
                        >
                          ✅ Release Coins
                        </button>
                        <button
                          onClick={() => setShowDisputeModal(true)}
                          className="bg-red-500 text-white py-3 px-6 rounded-xl w-full font-semibold shadow hover:bg-red-600 transition"
                        >
                          ⚠️ Raise Dispute
                        </button>
                      </>
                    )}
                  </div>
                );
              }

              return null;
            })()}
          </div>

          {/* ===== FEEDBACK (DESKTOP/TABLET) ===== */}
          {trade && isCompleted(trade.status) && currentUser && (
            <div className="mt-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 rounded-xl p-4 hidden sm:block">
              <h3 className="text-lg font-semibold text-blue-700 dark:text-blue-300 mb-2">Rate your counterparty</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                {(() => {
                  const isBuyerSide = currentUser?._id === trade?.buyer?._id;
                  const other = isBuyerSide ? trade?.seller : trade?.buyer;
                  return <>You’re leaving feedback for <strong>@{other?.username || 'user'}</strong>.</>;
                })()}
              </p>

              {myFb && !fbEditing ? (
                <div className="bg-white dark:bg-neutral-900 border dark:border-neutral-800 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-800 dark:text-gray-200">
                      <div className="font-medium">
                        {myFb.type === 'positive' && '👍 Positive'}
                        {myFb.type === 'neutral'  && '😐 Neutral'}
                        {myFb.type === 'negative' && '👎 Negative'}
                      </div>
                      {myFb.comment && <div className="text-gray-700 dark:text-gray-300 mt-1">{myFb.comment}</div>}
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Left on {new Date(myFb.createdAt || myFb.updatedAt).toLocaleString()}
                      </div>
                    </div>
                    <button
                      onClick={() => setFbEditing(true)}
                      className="ml-3 bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-blue-700"
                    >
                      Edit feedback
                    </button>
                  </div>
                  {fbMsg && <div className="text-sm text-green-700 dark:text-green-400 mt-2">{fbMsg}</div>}
                </div>
              ) : (
                <form onSubmit={myFb ? submitFeedbackUpdate : submitFeedbackCreate} className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {[
                      { v: 'positive', label: '👍 Positive' },
                      { v: 'neutral',  label: '😐 Neutral' },
                      { v: 'negative', label: '👎 Negative' },
                    ].map(opt => (
                      <label
                        key={opt.v}
                        className={`px-3 py-2 rounded-lg border cursor-pointer text-sm ${
                          fbType === opt.v
                            ? 'bg-white dark:bg-neutral-900 border-blue-500 text-blue-700 dark:text-blue-300'
                            : 'bg-white dark:bg-neutral-900 border-gray-300 dark:border-neutral-700 text-gray-700 dark:text-gray-200'
                        }`}
                      >
                        <input
                          type="radio"
                          name="fbType"
                          value={opt.v}
                          checked={fbType === opt.v}
                          onChange={() => setFbType(opt.v)}
                          className="mr-2"
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                      Comment (optional, max 500)
                    </label>
                    <textarea
                      value={fbComment}
                      onChange={(e) => setFbComment(e.target.value.slice(0, 500))}
                      rows={3}
                      className="w-full border rounded-lg px-3 py-2 bg-white dark:bg-neutral-900 border-gray-300 dark:border-neutral-700 text-gray-800 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-300 dark:focus:ring-blue-500"
                      placeholder="What went well? Anything to improve?"
                      maxLength={500}
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      type="submit"
                      disabled={fbSubmitting}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-60"
                    >
                      {fbSubmitting ? (myFb ? 'Updating…' : 'Submitting…') : (myFb ? 'Update Feedback' : 'Submit Feedback')}
                    </button>

                    {myFb && (
                      <button
                        type="button"
                        onClick={() => {
                          setFbType(myFb.type);
                          setFbComment(myFb.comment || '');
                          setFbEditing(false);
                          setFbMsg('');
                        }}
                        className="bg-gray-200 dark:bg-neutral-800 text-gray-800 dark:text-gray-100 px-4 py-2 rounded-lg hover:bg-gray-300 dark:hover:bg-neutral-700"
                      >
                        Cancel
                      </button>
                    )}

                    {fbMsg && <span className="text-sm text-red-600 dark:text-red-400">{fbMsg}</span>}
                  </div>
                </form>
              )}
            </div>
          )}
        </div>

        {/* Right Side: Chat */}
        <div className="bg-white dark:bg-neutral-900 border dark:border-neutral-800 rounded-xl shadow-md p-4 sm:p-5 flex flex-col h-full">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xl font-semibold text-blue-700 dark:text-blue-300">Trade Chat</h3>
            <button
              type="button"
              onClick={() => fetchMessages()}
              className="text-xs sm:text-sm bg-gray-100 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 px-3 py-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-neutral-700"
              title="Refresh chat"
            >
              Refresh
            </button>
          </div>

          <div
            ref={chatListRef}
            onScroll={onChatScroll}
            className="overflow-y-auto bg-gray-100 dark:bg-neutral-800 p-2 sm:p-3 rounded-md border dark:border-neutral-700 h-[260px] sm:h-[420px]"
            style={{
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {renderMessages.length > 0 ? (
              renderMessages.map((msg) => (
                <ChatRow
                  key={msgKey(msg)}
                  msg={msg}
                  currentUserId={currentUser?._id}
                  onOpenImage={openImage}
                />
              ))
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">No messages yet.</p>
            )}
          </div>

          {/* Input & Send Button */}
          <div className="mt-3 sm:mt-4">
            {/* Attachment preview (if selected) */}
            {attachment && (
              <div className="mb-2 flex items-center justify-between gap-2 bg-gray-50 dark:bg-neutral-800 border dark:border-neutral-700 rounded-lg px-3 py-2">
                <div className="flex items-center gap-3 min-w-0">
                  {/* Thumbnail preview (small) */}
                  {attachmentPreview ? (
                    <button
                      type="button"
                      onClick={() => openImage(attachmentPreview)}
                      className="shrink-0"
                      title="Open preview"
                    >
                      <img
                        src={attachmentPreview}
                        alt="preview"
                        className="w-12 h-12 rounded-md object-cover border border-gray-200 dark:border-neutral-700"
                        loading="eager"
                        draggable={false}
                      />
                    </button>
                  ) : (
                    <span className="text-lg">🖼️</span>
                  )}

                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">
                      {attachment.name || "Screenshot"}
                    </p>
                    <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">
                      {(attachment.size ? (attachment.size / 1024).toFixed(0) : "")} KB
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={clearAttachment}
                    className="text-xs px-2 py-1 rounded bg-gray-200 dark:bg-neutral-700 text-gray-800 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-neutral-600"
                  >
                    Remove
                  </button>
                </div>
              </div>
            )}

            <div className="flex gap-2 items-stretch">
              {/* Hidden file input (images/screenshots) */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files && e.target.files[0];
                  pickAttachment(f);
                  e.target.value = "";
                }}
              />

              <button
                type="button"
                onClick={() => fileInputRef?.current?.click()}
                className="w-11 sm:w-12 flex items-center justify-center rounded-lg border border-blue-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 hover:bg-gray-50 dark:hover:bg-neutral-800 transition"
                title="Attach screenshot"
              >
                📎
              </button>

              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={onMessageKeyDown}
                placeholder="Type your message… (Press Enter to send)"
                className="flex-1 border border-blue-200 dark:border-neutral-700 rounded-lg px-3 sm:px-4 py-2 text-xs sm:text-sm shadow-sm bg-white dark:bg-neutral-900 text-gray-800 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-300 dark:focus:ring-blue-500"
              />

              <button
                onClick={handleSend}
                disabled={sending || (!newMessage.trim() && !attachment)}
                className="bg-blue-600 text-white px-3 sm:px-5 py-2 rounded-lg hover:bg-blue-700 transition text-xs sm:text-sm disabled:opacity-60"
              >
                {sending ? 'Sending…' : 'Send'}
              </button>
            </div>

            <div className="mt-2 flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400">
              <span>Images are shown as small thumbnails — tap to open full view.</span>
              <button
                type="button"
                onClick={() => scrollToBottom('smooth')}
                className="underline hover:opacity-80"
              >
                Jump to latest
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ===== ACTION BUTTONS (MOBILE-ONLY BELOW CHAT) ===== */}
      <div className="max-w-7xl mx-auto mt-3 sm:hidden">
        {(() => {
          const status = trade?.status;
          const iAmBuyer =
            currentUser?._id && trade?.buyer?._id && currentUser._id === trade.buyer._id;
          const iAmSeller =
            currentUser?._id && trade?.seller?._id && currentUser._id === trade.seller._id;

          const raisedBy =
            trade?.dispute?.raisedBy?._id || trade?.dispute?.raisedBy;
          const iAmDisputeRaiser =
            raisedBy && currentUser?._id && raisedBy.toString() === currentUser._id.toString();
          const disputeOpen = trade?.dispute?.status === 'open';

          if (status === 'disputed') {
            if (iAmDisputeRaiser && disputeOpen) {
              return (
                <div className="grid grid-cols-1 gap-2">
                  <button
                    onClick={async () => {
                      try {
                        await p2pAxios.post('/trades/disputes/cancel', { tradeId: trade._id });
                        setTrade(prev => ({
                          ...prev,
                          status: 'paid',
                          dispute: prev?.dispute ? { ...prev.dispute, status: 'cancelled' } : prev?.dispute
                        }));
                      } catch (err) {
                        console.error('❌ Error cancelling dispute:', err);
                      }
                    }}
                    className={`${mobileBtn} bg-yellow-500 text-white hover:bg-yellow-600`}
                  >
                    🛑 Cancel Dispute
                  </button>
                </div>
              );
            }
            return null;
          }

          if (status === 'initiated' && iAmBuyer) {
            return (
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={markPaid}
                  className={`${mobileBtn} bg-yellow-500 text-white hover:bg-yellow-600`}
                >
                  ✅ Mark as Paid
                </button>
                <button
                  onClick={handleCancel}
                  className={`${mobileBtn} bg-red-600 text-white hover:bg-red-700`}
                >
                  ❌ Cancel
                </button>
              </div>
            );
          }

          if (status === 'paid') {
            return (
              <div className="grid grid-cols-2 gap-2">
                {iAmBuyer && (
                  <>
                    <button
                      onClick={handleCancel}
                      className={`${mobileBtn} bg-red-600 text-white hover:bg-red-700`}
                    >
                      ❌ Cancel
                    </button>
                    <button
                      onClick={() => setShowDisputeModal(true)}
                      className={`${mobileBtn} bg-red-500 text-white hover:bg-red-600`}
                    >
                      ⚠️ Dispute
                    </button>
                  </>
                )}
                {iAmSeller && (
                  <>
                    <button
                      onClick={releaseUSDT}
                      className={`${mobileBtn} bg-green-600 text-white hover:bg-green-700`}
                    >
                      ✅ Release
                    </button>
                    <button
                      onClick={() => setShowDisputeModal(true)}
                      className={`${mobileBtn} bg-red-500 text-white hover:bg-red-600`}
                    >
                      ⚠️ Dispute
                    </button>
                  </>
                )}
              </div>
            );
          }

          return null;
        })()}
      </div>

      {showDisputeModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-neutral-900 border dark:border-neutral-800 p-6 rounded-lg w-full max-w-md shadow-lg">
            <h2 className="text-lg font-bold mb-4 text-center text-red-600">⚠️ Raise a Dispute</h2>

            <label className="block mb-2 font-medium text-gray-800 dark:text-gray-200">Select a reason:</label>
            <select
              value={selectedReason}
              onChange={(e) => setSelectedReason(e.target.value)}
              className="w-full border px-3 py-2 rounded mb-4 focus:ring-2 focus:ring-red-300 dark:focus:ring-red-500 bg-white dark:bg-neutral-900 border-gray-300 dark:border-neutral-700 text-gray-800 dark:text-gray-100"
            >
              <option value="">-- Select --</option>
              <option value="Buyer did not make the payment">Buyer did not make the payment</option>
              <option value="Buyer marked as paid without actual payment">Buyer marked as paid without actual payment</option>
              <option value="Buyer sent payment from a third-party account">Buyer sent payment from a third-party account</option>
              <option value="Seller is unresponsive">Seller is unresponsive</option>
              <option value="Seller is refusing to release the crypto after payment">Seller is refusing to release the crypto after payment</option>
              <option value="Accidental trade or wrong trade initiated">Accidental trade or wrong trade initiated</option>
              <option value="Other">Other (Please specify)</option>
            </select>

            {selectedReason === 'Other' && (
              <input
                type="text"
                placeholder="Enter your reason..."
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                className="w-full border px-3 py-2 rounded mb-4 focus:ring-2 focus:ring-red-300 dark:focus:ring-red-500 bg-white dark:bg-neutral-900 border-gray-300 dark:border-neutral-700 text-gray-800 dark:text-gray-100"
              />
            )}

            <div className="flex justify-between mt-4 gap-3">
              <button
                onClick={() => setShowDisputeModal(false)}
                className="bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-500 w-full"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const reasonToSend = selectedReason === 'Other' ? customReason : selectedReason;
                  if (!reasonToSend.trim()) {
                    alert("⚠️ Please select or enter a reason.");
                    return;
                  }

                  p2pAxios.post('/trades/disputes/raise', {
                    tradeId: trade._id,
                    reason: reasonToSend.trim(),
                  })
                    .then(() => {
                      fetchTrade();
                      setShowDisputeModal(false);
                      setSelectedReason('');
                      setCustomReason('');
                    })
                    .catch((err) => {
                      console.error("❌ Error raising dispute:", err);
                      alert(err.response?.data?.message || "Failed to raise dispute.");
                    });
                }}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 w-full"
              >
                Raise Dispute
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default P2PTradeChat;
